import React, { useEffect } from 'react';
import chunk from 'lodash/chunk';
import styled, { StylesProps } from '../../../lib/styles';
import { Article } from '../../../models/article';
import Ad, { Ads } from '../../../components/presentational/Ad';
import Typography from '../../../components/presentational/Typography';
import Title from '../Title';
import MainImage from '../MainImage';
import Video from '../Video';
import Spacer from '../../../components/presentational/Spacer';
import Slider from '../../../components/presentational/Slider';
import ProviderCTA from '../ProviderCTA';
import AdhesionAd from '../AdhesionAd';
import BlockUpdates from '../../../behaviour/BlockUpdates';
import { trackEvent } from '../../../lib/ga';
import theme from '../../../lib/theme';

import sponsoredArticleLayoutStyles from './SponsoredArticleLayout_styles';
import { raiseEvent, SPONSORED_ARTICLE_VIEW } from '../../../lib/gtm';
import ArticleSharing from '../ArticleSharing';
import { SHARE } from '../lib/ga';

const { layoutWidths } = theme;
export interface SponsoredArticleLayoutProps {
  ads: Ads;
  article: Article;
  hideComments?: boolean;
  hideAds?: boolean;
  showLinkbyPubFeed?: boolean;
}

const SPLIT_THRESHOLD = 12;
const SponsoredArticleLayout = ({
  ads,
  //  @ts-ignore
  hideComments,
  hideAds,
  article: {
    id,
    //  @ts-ignore
    article_question,
    sponsored_brand,
    title,
    //  @ts-ignore
    link,
    author,
    category,
    created,
    youtube_video,
    large_image,
    image_caption,
    content,
    gallery,
    slug,
    provider,
  },
  showLinkbyPubFeed,
  styles,
}: SponsoredArticleLayoutProps &
  StylesProps<typeof sponsoredArticleLayoutStyles>) => {

  useEffect(() => {
    // console.log('SponsoredArticleLayout', { ads });
    raiseEvent({
      ...SPONSORED_ARTICLE_VIEW,
      ...{
        sasEventType: `SPONSORED_ARTICLE_VIEW: ${category}`,
      },
    });
  }, []);

  const halves = chunk(
    content,
    content.length >= SPLIT_THRESHOLD
      ? Math.ceil(content.length / 2)
      : content.length
  );

  // console.log(
  //   ads,
  //   hideComments,
  //   hideAds,

  //   article_question,
  //   sponsored_brand,
  //   title,
  //   link,
  //   author,
  //   category,
  //   created,
  //   youtube_video,
  //   large_image,
  //   image_caption,
  //   content,
  //   gallery,
  //   slug,
  //   provider,

  //   styles
  // );

  return (
    <Spacer key={id} space={hideAds ? 's' : 'm'} maxWidth={layoutWidths.contentPane} align={'center'}
      className={`sponsored-layout`}
    >
      {halves.map((pars: string[], i: number) => {
        const isFirstHalf = i === 0;
        const isSecondHalf = i === halves.length - 1;
        const inarticleAd = ads[`sponsored_inarticle${i + 1}`];
        const stickyAd = ads[`sponsored_siderail${i + 1}`];
        const halvedPars = chunk(pars, 2);

        return (
          <Spacer key={i} width={layoutWidths.contentPane} space={'m'}>
            {!hideAds && !isFirstHalf && isSecondHalf && ads && ads.sponsored_inbetween && (
              <Ad ad={ads.sponsored_inbetween} lazy initialSize={[300, 90]} gutter={'s'} />
            )}

            {/*
              Content column must be capped at contentPane - sidebar, exactly as
              StandardArticleLayout does. It was maxWidth:'100%', which resolves against
              the flex ROW — not against the space left over once the side-rail is
              accounted for — so the column never reserved room for the rail.

              In the first half the prose happened to be narrow enough that flex-grow
              settled it at the right width and it looked fine. In the second half a
              child that will not shrink (the Linkby widget) pushed the column's minimum
              size out to the full row width, the column took all 1134px, and the rail —
              which could not shrink either — was shoved 361px past the right margin.
              That is the "content goes way too wide after the inbetween ad" report: the
              inbetween ad is simply the first thing in the second half, not the cause.
            */}
            <Spacer relative row space={'m'} maxWidth={layoutWidths.contentPane} align={'stretch'}>
              <Spacer
                flex={'auto'}
                maxWidth={layoutWidths.contentPane - layoutWidths.sidebar}
                space={'l'}
              >
                <Spacer space={'s'}>
                  {isFirstHalf && (
                    <Spacer space={'s'}>
                      <Title
                        title={title}
                        author={author}
                        sponsored={true}
                        sponsored_brand={sponsored_brand}
                        category={category}
                        created={created}
                        link={link}
                      />

                      {!!youtube_video && (
                        <Video
                          video_id={youtube_video}
                          bg_url={large_image}
                          caption={image_caption}
                        />
                      )}

                      {!!(gallery && gallery.length) && !youtube_video && (
                        <Slider
                          brand={'sas'}
                          images={gallery}
                          aspectRatio={9 / 16}
                          minWidth={300}
                          maxWidth={751}
                          onPreviousSlide={() =>
                            trackEvent({
                              action: 'PREVIOUS SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                          onNextSlide={() =>
                            trackEvent({
                              action: 'NEXT SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                          onGotoSlide={() =>
                            trackEvent({
                              action: 'GOTO SLIDE',
                              category: 'ARTICLE',
                              label: slug,
                            })
                          }
                        />
                      )}

                      {!!large_image &&
                        !youtube_video &&
                        !(gallery && gallery.length) && (
                          <MainImage
                            bg_url={large_image}
                            caption={image_caption}
                          />
                        )}
                    </Spacer>
                  )}

                  <Spacer key={i} space={'s'}>
                    {halvedPars.map((pars: string[], j: number) => (
                      <React.Fragment key={j}>
                        {isFirstHalf &&
                          j === 1 &&
                          !hideAds &&
                          inarticleAd && (
                          <Ad ad={inarticleAd} lazy initialSize={[300, 90]} />
                        )}

                        <BlockUpdates
                          block
                          render={() => (
                            <Typography
                              dangerouslySetInnerHTML={{
                                __html: pars.join('\n'),
                              }}
                              size={'l'}
                              brand={'sas'}
                              family={'copy'}
                            />
                          )}
                        />
                      </React.Fragment>
                    ))}
                  </Spacer>
                </Spacer>

                {isSecondHalf && provider && (
                  <Spacer space={['l', 'xl']}>
                    {provider && provider.cta_text && (
                      // Was a hard 851px — WIDER than the 820px content column now caps at,
                      // so it would hang 31px over the side-rail. Fluid instead.
                      <Spacer width={'100%'}>
                        <ProviderCTA {...provider} brand={'sas'} />
                      </Spacer>
                    )}
                  </Spacer>
                )}

                {!hideAds && isSecondHalf && !isFirstHalf && inarticleAd && (
                  <Ad ad={inarticleAd} lazy initialSize={[300, 90]} />
                )}

                {isSecondHalf && (
                  <>
                    <ArticleSharing
                      link={link}
                      brand="sas"
                      onShare={network =>
                        trackEvent(
                          { ...SHARE, label: network },
                          { transport: 'beacon' }
                        )
                      }
                    />

                    {showLinkbyPubFeed && (
                      // This carried a hard width={1160} — the full contentPane — while
                      // living INSIDE the content column. That is what refused to shrink
                      // and drove the column's minimum size out to the whole row, pushing
                      // the side-rail off the page. StandardArticleLayout's equivalent
                      // block sets no width at all; match it.
                      <Spacer width={'100%'} space={'s'}>
                        <Spacer relative row space={'m'} align={'stretch'}>
                          <Spacer flex={'auto'} width={'100%'} space={'m'}>
                            <div className="linkby-widget" data-type="listicle"></div>
                          </Spacer>
                        </Spacer>
                      </Spacer>
                    )}
                  </>
                )}
              </Spacer>

              {/*
                `width` (-> flex: 0 1 auto), not `flex: 0 0 <sidebar>px`, so the rail can
                SHRINK — again matching StandardArticleLayout. A rigid basis meant that
                when the content column refused to give ground, the rail had nowhere to
                go but off the right edge. Standard's rail settles at ~280px inside a
                1134px row rather than its nominal 340px, and that give is exactly what
                keeps the row intact.
              */}
              <Spacer className={styles.lOnly} width={layoutWidths.sidebar} relative align={'center'}>
                {!hideAds && stickyAd && (
                  <Ad
                    ad={stickyAd}
                    sticky
                    lazy
                    align={'flex-end'}
                    backgroundColor={'transparent'}
                    initialSize={[120, 600]}
                  />
                )}
              </Spacer>
            </Spacer>
          </Spacer>
        );
      })}

      {!hideAds && ads && ads.sponsored_adhesion && <AdhesionAd ad={ads.sponsored_adhesion} />}
    </Spacer>
  );
};

export default styled(sponsoredArticleLayoutStyles)(SponsoredArticleLayout);
